import { Menu, X } from 'lucide-react';
import { Link, useNavigate } from 'react-router';

import { BrandLogo } from '@/components/brand/BrandLogo';
import { Button } from '@/components/ui/button';
import { brand } from '@/config/brand';
import { HEADER_NAV, type NavAuth, type NavItem } from '@/config/nav';
import { usePricingVisible } from '@/features/landing/hooks/useBetaFreePeriod';
import { useDisclosure } from '@/hooks/useDisclosure';
import { selectIsAuthenticated, useAuthStore } from '@/stores/auth';

const NAV_LINK =
  'text-sm font-medium text-muted-foreground transition-colors hover:text-foreground';

/**
 * FO 전역 헤더 — sticky + blur, 로고 + 좌측 GNB + 우측 인증 액션.
 * 항목은 config/nav.ts 선언 배열 SSoT — slot·auth 필터로 데스크톱·모바일 양쪽 렌더.
 * lg(1024px) 기준 GNB ↔ 메뉴 버튼 전환. 높이는 spacing 토큰 h-header.
 */
export function FoHeader() {
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const { isOpen, close, toggle } = useDisclosure();
  const pricingVisible = usePricingVisible();

  const isVisible = (auth: NavAuth) =>
    auth === 'always' ? true : auth === 'guest' ? !isAuthenticated : isAuthenticated;
  const bySlot = (slot: NavItem['slot']) =>
    HEADER_NAV.filter(
      (i) => i.slot === slot && isVisible(i.auth) && (!i.pricing || pricingVisible),
    ).sort((a, b) => a.order - b.order);
  const menuItems = bySlot('menu');
  const actionItems = bySlot('action');

  async function onLogout() {
    close();
    await logout();
    navigate('/');
  }

  return (
    <header className="sticky top-0 z-header border-b bg-card/90 backdrop-blur">
      <div className="mx-auto flex h-header max-w-content items-center gap-6 px-4 sm:px-gutter">
        <Link to="/" className="flex items-center" aria-label={brand.name} onClick={close}>
          <BrandLogo />
        </Link>

        <nav className="hidden gap-1 lg:flex" aria-label="주 메뉴">
          {menuItems.map((item) => (
            <Link
              key={item.label}
              to={item.route!}
              className={`px-3 py-2 whitespace-nowrap ${NAV_LINK}`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          {actionItems.map((item) =>
            item.kind === 'logout' ? (
              <Button
                key={item.label}
                variant="ghost"
                size="sm"
                className="hidden lg:inline-flex"
                onClick={onLogout}
              >
                {item.label}
              </Button>
            ) : (
              <Button
                key={item.label}
                variant={item.button ?? 'ghost'}
                size="sm"
                className="hidden lg:inline-flex"
                asChild
              >
                <Link to={item.route!}>{item.label}</Link>
              </Button>
            ),
          )}

          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            aria-label={isOpen ? '메뉴 닫기' : '메뉴 열기'}
            aria-expanded={isOpen}
            onClick={toggle}
          >
            {isOpen ? <X /> : <Menu />}
          </Button>
        </div>
      </div>

      {isOpen && (
        <nav
          className="absolute inset-x-0 top-header flex flex-col items-stretch border-b bg-card px-4 pt-1 pb-4 shadow-md lg:hidden"
          aria-label="모바일 메뉴"
        >
          {menuItems.map((item) => (
            <Link
              key={`m-${item.label}`}
              to={item.route!}
              className="border-b py-3 text-base font-medium"
              onClick={close}
            >
              {item.label}
            </Link>
          ))}
          {actionItems.map((item) =>
            item.kind === 'logout' ? (
              <button
                key={`ma-${item.label}`}
                type="button"
                className="border-b py-3 text-left text-base font-medium"
                onClick={onLogout}
              >
                {item.label}
              </button>
            ) : (
              <Button
                key={`ma-${item.label}`}
                variant={item.button ?? 'outline'}
                className="mt-3 w-full"
                asChild
              >
                <Link to={item.route!} onClick={close}>
                  {item.label}
                </Link>
              </Button>
            ),
          )}
        </nav>
      )}
    </header>
  );
}
